import React, { FC, useEffect, useState } from "react";
import styles from "./AuthLayout.module.scss";
import Notifier from "@features/common/notifier/Notifier";
import { Outlet, useNavigate, useSearchParams } from "react-router-dom";
import LoginFooter from "@features/common/footer/loginFooter/LoginFooter";
import { useGetTokenMutation } from "features/auth/authApi";
import { useAppDispatch } from "@redux/hooks";
import { setoAuthAccessToken } from "@redux/slices/AuthSlice";
import PageLoader from "@features/common/pageLoader/PageLoader";
import NoInternet from "@features/common/noInternet/NoInternet";

const AuthLayout = () => {
  /* --------------------------------------------------------------------------
   * Hooks
   * -------------------------------------------------------------------------- */
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  // No Internet logic
  const [hasInternet, setHasInternet] = useState(window.navigator.onLine);

  useEffect(() => {
    const updateNetwork = () => setHasInternet(window.navigator.onLine);
    window.addEventListener("offline", updateNetwork);
    window.addEventListener("online", updateNetwork);
    return () => {
      window.removeEventListener("offline", updateNetwork);
      window.removeEventListener("online", updateNetwork);
    };
  }, []);

  /* --------------------------------------------------------------------------
   * API Mutation
   * -------------------------------------------------------------------------- */
  /**
   * OAuth token mutation for client credentials grant
   * Retrieves access token required for authentication APIs
   */
  const [getToken, { data, isSuccess, isLoading }] = useGetTokenMutation();

  /* --------------------------------------------------------------------------
   * Effects
   * -------------------------------------------------------------------------- */
  /**
   * Fetch OAuth access token on component mount
   * Uses client credentials from environment variables
   * Required for subsequent authentication API calls
   */
  useEffect(() => {
    getToken({
      client_id: import.meta.env.VITE_MYCREDITS_CONSOLE_OAUTH_CLIENT_ID,
      client_secret: import.meta.env.VITE_MYCREDITS_CONSOLE_OAUTH_CLIENT_SECRET,
      grant_type: "client_credentials",
    });
  }, []);

  /**
   * Store OAuth access token in Redux state when successfully fetched
   * Makes token available globally for authenticated API requests
   */
  useEffect(() => {
    if (isSuccess && data?.access_token) {
      dispatch(setoAuthAccessToken(data.access_token));
    }
  }, [data, isSuccess, dispatch]);

  if (isLoading) {
    return (
      <div style={{}}>
        <PageLoader />
      </div>
    );
  }

  /* --------------------------------------------------------------------------
   * Render
   * -------------------------------------------------------------------------- */
  return (
    <div
      className={`signin-signup ${styles["loginlayout"]}`}
      style={{
        backgroundImage: `url(/images/login-bg-image.png)`,
      }}
    >
      {/* Toast Notifications */}
      <Notifier />

      {/* No Internet Banner */}
      <NoInternet isOnline={hasInternet} />

      {/* Child Auth Routes (Login, Signup, etc.) */}
      <Outlet context={{ redirectUrl: searchParams.get("redirect") }} />

      {/* Footer with Privacy Policy and Terms Links */}
      <LoginFooter />
    </div>
  );
};;

export default AuthLayout;
